<template>
  <div
    class="editable-element"

    ref="elementRef"
    :id="`editable-element-${elementInfo.id}`"
    :style="{
      width: '100%', height: isSlideLayout ? '100%' : 'auto',
      ...(elementInfo.type !== 'shape' && elementInfo.type !== 'line' && elementInfo.type !== 'section'  ? { position: 'relative' } : { position: 'absolute' })
    }"
  >
    <component
      :is="currentElementComponent"
      :elementInfo="elementInfo"
      :selectElement="selectElement"
      :contextmenus="contextmenus"
      :sectionId="sectionId"
      :sectionRowId="sectionRowId"
      :sectionStyle="sectionStyle"
      :sectionUsePrototypeTile="sectionUsePrototypeTile"
      :sectionPrototypeTileStyle="sectionPrototypeTileStyle"
      :style="{ width: '100%', height: isSlideLayout ? '100%' : 'auto' }"
    ></component>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType } from 'vue'
import { ElementTypes, PPTElement } from '@/types/slides'
import { ContextmenuItem } from '@/components/Contextmenu/types'

import useLockElement from '@/hooks/useLockElement'
import useDeleteElement from '@/hooks/useDeleteElement'
import useOrderElement from '@/hooks/useOrderElement'
import useAlignElementToCanvas from '@/hooks/useAlignElementToCanvas'
import useCopyAndPasteElement from '@/hooks/useCopyAndPasteElement'
import useSelectAllElement from '@/hooks/useSelectAllElement'


import { ElementOrderCommands, ElementAlignCommands } from '@/types/edit'

import ImageElement from '@/views/components/element/ImageElement/index.vue'
import TextElement from '@/views/components/element/TextElement/index.vue'
import HtmlElement from '@/views/components/element/HtmlElement/index.vue'
import DragDropElement from '@/views/components/element/DragDropElement/index.vue'
import SlideLayoutElement from '@/views/components/element/SlideLayoutElement/index.vue'
import ShapeElement from '@/views/components/element/ShapeElement/index.vue'
import LineElement from '@/views/components/element/LineElement/index.vue'
import ChartElement from '@/views/components/element/ChartElement/index.vue'
import SectionElement from '@/views/components/element/SectionElement/index.vue'
import TableElement from '@/views/components/element/TableElement/index.vue'
import LatexElement from '@/views/components/element/LatexElement/index.vue'
import VideoElement from '@/views/components/element/VideoElement/index.vue'
import AudioElement from '@/views/components/element/AudioElement/index.vue'

const props = defineProps({
  elementInfo: {
    type: Object as PropType<PPTElement>,
    required: true,
  },
  sectionId: {
    type: String,
  },
  sectionRowId: {
    type: String,
  },
  elementIndex: {
    type: Number,
    required: true,
  },
  isMultiSelect: {
    type: Boolean,
    required: true,
  },
  selectElement: {
    type: Function as PropType<(e: MouseEvent | TouchEvent, element: PPTElement, canMove?: boolean) => void>,
    required: true,
  },
  openLinkDialog: {
    type: Function as PropType<() => void>,
    required: true,
  },
  sectionStyle: {
    type: Object
  },
  sectionUsePrototypeTile: {
    type: Boolean
  },
  sectionPrototypeTileStyle: {
    type: Object
  }
})

const isSlideLayout = computed(() => ElementTypes.SLIDE_LAYOUT === props.elementInfo.type)

const currentElementComponent = computed(() => {
  const elementTypeMap = {
    [ElementTypes.IMAGE]: ImageElement,
    [ElementTypes.TEXT]: TextElement,
    [ElementTypes.HTML]: HtmlElement,
    [ElementTypes.DRAG_DROP]: DragDropElement,
    [ElementTypes.SLIDE_LAYOUT]: SlideLayoutElement,
    [ElementTypes.SHAPE]: ShapeElement,
    [ElementTypes.LINE]: LineElement,
    [ElementTypes.CHART]: ChartElement,
    [ElementTypes.SECTION]: SectionElement,
    [ElementTypes.TABLE]: TableElement,
    [ElementTypes.LATEX]: LatexElement,
    [ElementTypes.VIDEO]: VideoElement,
    [ElementTypes.AUDIO]: AudioElement,
  }
  return elementTypeMap[props.elementInfo.type] || null
})

const { orderElement } = useOrderElement()
const { alignElementToCanvas } = useAlignElementToCanvas()
const { deleteElement } = useDeleteElement()
const { lockElement, unlockElement } = useLockElement()
const { copyElement, pasteElement, cutElement } = useCopyAndPasteElement()
const { selectAllElement } = useSelectAllElement()

const contextmenus = (): ContextmenuItem[] => {
  if (props.elementInfo.lock) {
    return [{
      text: 'Unlock',
      handler: () => unlockElement(props.elementInfo),
    }]
  }

  return [
    {
      text: 'Cut',
      subText: 'Ctrl + X',
      handler: cutElement,
    },
    {
      text: 'Copy',
      subText: 'Ctrl + C',
      handler: copyElement,
    },
    {
      text: 'Paste',
      subText: 'Ctrl + V',
      handler: pasteElement,
    },
    { divider: true },
    {
      text: 'Center on Page',
      children: [
        { text: 'Horizontally', handler: () => alignElementToCanvas(ElementAlignCommands.HORIZONTAL) },
        { text: 'Vertically', handler: () => alignElementToCanvas(ElementAlignCommands.VERTICAL) }
      ],
    },
    {
      text: 'Order ',
      disable: props.isMultiSelect && !props.elementInfo.groupId,
      handler: () => orderElement(props.elementInfo, ElementOrderCommands.TOP),
      children: [
        { text: 'Bring to Front', handler: () => orderElement(props.elementInfo, ElementOrderCommands.TOP) },
        { text: 'Bring Forward', handler: () => orderElement(props.elementInfo, ElementOrderCommands.UP) },
        { text: 'Send to Back', handler: () => orderElement(props.elementInfo, ElementOrderCommands.BOTTOM) },
        { text: 'Send Backward', handler: () => orderElement(props.elementInfo, ElementOrderCommands.DOWN) },
      ],
    },
    { divider: true },
    {
      text: 'Select All',
      subText: 'Ctrl + A',
      handler: selectAllElement,
    },
    {
      text: 'Locking',
      subText: 'Ctrl + L',
      handler: lockElement,
    },
    {
      text: 'Delete',
      subText: 'Delete',
      handler: deleteElement,
    },
  ]
}
</script>
<style scoped>
.editable-element {
  top: 0;
}
</style>
